import { Button, Divider, Modal, ModalClose, ModalDialog, ToggleButtonGroup, Typography } from '@healthinal/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { getGetAppointmentsForPatientWithoutLabOrderQueryKey } from '../../../api/generated/appointment-controller/appointment-controller.ts';
import { AppointmentDto, AppointmentDtoType, LabOrderDtoType } from '../../../api/generated/model';
import { getLabOrderTypeName } from '../../../utils/labOrder.ts';
import { CreateQuantiferonResultForm } from './CreateQuantiferonResultForm.tsx';
import { CreateSputumResultForm } from './CreateSputumResultForm.tsx';
import { CreateTspotResultForm } from './CreateTspotResultForm.tsx';
import { CreateTuberculinResultForm } from './CreateTuberculinResultForm.tsx';
import { CreateXrayResultForm } from './CreateXrayResultForm.tsx';

export function CreateLabOrderResultDialog({
  open,
  onClose,
  appointment,
  patientId,
}: {
  open: boolean;
  onClose: () => void;
  appointment?: AppointmentDto | null;
  patientId: string;
}) {
  const queryClient = useQueryClient();
  const [selectedTestType, setSelectedTestType] = useState<LabOrderDtoType | undefined>(undefined);

  const isTestTypeActive = (testType: LabOrderDtoType, appointmentType: AppointmentDtoType | undefined) => {
    if (appointmentType === undefined || appointmentType === AppointmentDtoType.OTHER) return true;

    return getAppointmentTypeForTestType(testType) === appointmentType;
  };

  useEffect(() => {
    if (appointment) {
      setSelectedTestType(getDefaultTestTypeForAppointmentType(appointment.type));
    }
  }, [appointment]);

  const handleOnClose = (reason?: string) => {
    if (reason === 'backdropClick') {
      return;
    }
    setSelectedTestType(undefined);
    void queryClient.invalidateQueries({ queryKey: getGetAppointmentsForPatientWithoutLabOrderQueryKey(patientId) });
    onClose();
  };
  return (
    <Modal open={open} onClose={(_, reason) => handleOnClose(reason)}>
      <ModalDialog sx={{ width: 730, overflowY: 'scroll' }}>
        <ModalClose />
        <Typography level={'h3'}>Neues Testresultat</Typography>
        <ToggleButtonGroup value={selectedTestType}>
          {Object.values(LabOrderDtoType).map((type) => (
            <Button
              disabled={!isTestTypeActive(type, appointment?.type)}
              value={type}
              key={type}
              onClick={() => setSelectedTestType(type)}>
              {getLabOrderTypeName(type)}
            </Button>
          ))}
        </ToggleButtonGroup>
        <Divider />
        {selectedTestType === LabOrderDtoType.BLOOD_QUANTIFERON && (
          <CreateQuantiferonResultForm appointment={appointment} patientId={patientId} onClose={handleOnClose} />
        )}
        {selectedTestType === LabOrderDtoType.BLOOD_TSPOT && (
          <CreateTspotResultForm appointment={appointment} patientId={patientId} onClose={handleOnClose} />
        )}
        {selectedTestType === LabOrderDtoType.TUBERCULIN && (
          <CreateTuberculinResultForm appointment={appointment} patientId={patientId} onClose={handleOnClose} />
        )}
        {selectedTestType === LabOrderDtoType.SPUTUM && (
          <CreateSputumResultForm appointment={appointment} patientId={patientId} onClose={handleOnClose} />
        )}
        {selectedTestType === LabOrderDtoType.XRAY && (
          <CreateXrayResultForm appointment={appointment} patientId={patientId} onClose={handleOnClose} />
        )}
      </ModalDialog>
    </Modal>
  );
}

const getAppointmentTypeForTestType = (testType: LabOrderDtoType) => {
  switch (testType) {
    case LabOrderDtoType.BLOOD_QUANTIFERON:
    case LabOrderDtoType.BLOOD_TSPOT:
      return AppointmentDtoType.BLOOD_SAMPLING;
    case LabOrderDtoType.SPUTUM:
      return AppointmentDtoType.SPUTUM;
    case LabOrderDtoType.TUBERCULIN:
      return AppointmentDtoType.TUBERCULIN;
    case LabOrderDtoType.XRAY:
      return AppointmentDtoType.XRAY;
  }
};

const getDefaultTestTypeForAppointmentType = (appointmentType: AppointmentDtoType) => {
  switch (appointmentType) {
    case AppointmentDtoType.BLOOD_SAMPLING:
      return LabOrderDtoType.BLOOD_TSPOT;
    case AppointmentDtoType.XRAY:
      return LabOrderDtoType.XRAY;
    case AppointmentDtoType.SPUTUM:
      return LabOrderDtoType.SPUTUM;
    case AppointmentDtoType.TUBERCULIN:
      return LabOrderDtoType.TUBERCULIN;
    case AppointmentDtoType.OTHER:
      return LabOrderDtoType.SPUTUM;
  }
};
